/**
 * Page principale de gestion des bundles avec upsell/downsell
 * Permet de créer, modifier, supprimer et gérer les bundles de produits
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  Squares2X2Icon,
  DocumentDuplicateIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { message, Modal, Button, Table, Tag, Space, Input, Select, Card, Row, Col, Statistic } from 'antd';

// Importation des services
import bundleService from '../services/bundleService';
import BundleForm from '../components/bundles/BundleForm';
import BundleAnalytics from '../components/bundles/BundleAnalytics';
import usePlan from '../hooks/usePlan';
import { getDeviseActive } from '../utils/devise';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

const Bundles = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list');
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBundleForm, setShowBundleForm] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    bundle_type: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [stats, setStats] = useState({
    total_bundles: 0,
    active_bundles: 0,
    total_views: 0,
    total_purchases: 0
  });

  const { canCreate } = usePlan();

  // Charger les bundles au montage et lors de changement de filtres
  useEffect(() => {
    loadBundles();
  }, [filters, pagination.current, pagination.pageSize]);

  // Charger les statistiques au montage
  useEffect(() => {
    loadStats();
  }, []);

  /**
   * Charger la liste des bundles avec filtres et pagination
   */
  const loadBundles = async () => {
    try {
      setLoading(true);
      const queryParams = {
        ...filters,
        page: pagination.current,
        pageSize: pagination.pageSize,
        include_items: true,
        include_analytics: true
      };

      const response = await bundleService.getAllBundles(queryParams);
      
      setBundles(response.bundles || []);
      setPagination(prev => ({
        ...prev,
        total: response.totalCount || 0
      }));

    } catch (error) {
      console.error('Erreur lors du chargement des bundles:', error);
      message.error('Erreur lors du chargement des bundles');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charger les statistiques des bundles
   */
  const loadStats = async () => {
    try {
      const response = await bundleService.getBundleStats();
      setStats(response);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  /**
   * Gérer la création d'un nouveau bundle avec vérification des limites
   */
  const handleNewBundle = async () => {
    try {
      // Vérifier les limites du plan
      // const limitCheck = await canCreate('bundles', bundles.length);
      
      // if (!limitCheck.allowed) {
      //   message.warning(
      //     `Limite atteinte: ${limitCheck.current}/${limitCheck.limit} bundles autorisés sur votre plan.`
      //   );
      //   return;
      // }

      setEditingBundle(null);
      setShowBundleForm(true);
    } catch (error) {
      console.error('Erreur lors de la vérification des limites:', error);
      message.error('Erreur lors de la vérification des limites');
    }
  };

  /**
   * Gérer l'édition d'un bundle
   */
  const handleEditBundle = (bundle) => {
    setEditingBundle(bundle);
    setShowBundleForm(true);
  };

  /**
   * Gérer la sauvegarde d'un bundle
   */
  const handleSaveBundle = async (bundleData) => {
    try {
      if (editingBundle) {
        await bundleService.updateBundle(editingBundle.id, bundleData);
        message.success('Bundle mis à jour avec succès');
      } else {
        await bundleService.createBundle(bundleData);
        message.success('Bundle créé avec succès');
      }
      
      setShowBundleForm(false);
      setEditingBundle(null);
      loadBundles();
      loadStats();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error(error.response?.data?.error || 'Erreur lors de la sauvegarde du bundle');
    }
  };

  /**
   * Gérer la suppression d'un bundle
   */
  const handleDeleteBundle = (bundle) => {
    confirm({
      title: 'Supprimer ce bundle ?',
      content: `Êtes-vous sûr de vouloir supprimer le bundle "${bundle.name}" ? Cette action est irréversible.`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await bundleService.deleteBundle(bundle.id);
          message.success('Bundle supprimé avec succès');
          loadBundles();
          loadStats();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          message.error('Erreur lors de la suppression du bundle');
        }
      },
    });
  };

  /**
   * Gérer la duplication d'un bundle
   */
  const handleDuplicateBundle = async (bundle) => {
    try {
      await bundleService.duplicateBundle(bundle.id);
      message.success('Bundle dupliqué avec succès');
      loadBundles();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      message.error('Erreur lors de la duplication du bundle');
    }
  };

  /**
   * Colonnes de la table des bundles
   */
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (text, record) => (
        <div>
          <div className="font-medium text-gray-900">{text}</div>
          <div className="text-sm text-gray-500">{record.description}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'bundle_type',
      key: 'bundle_type',
      width: '12%',
      render: (type) => {
        const colors = {
          standard: 'blue',
          promotional: 'green',
          seasonal: 'orange'
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => {
        const colors = {
          active: 'green',
          inactive: 'red',
          draft: 'orange'
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Articles',
      dataIndex: 'bundle_items',
      key: 'items_count',
      width: '10%',
      render: (items) => (
        <div className="text-center">
          {items?.length || 0}
          <div className="text-xs text-gray-500">
            {items?.filter(item => item.item_type === 'main').length || 0} principaux
          </div>
        </div>
      ),
    },
    {
      title: 'Prix calculé',
      dataIndex: 'calculated_total_price',
      key: 'price',
      width: '12%',
      render: (price) => (
        <div className="font-medium text-right">
          {new Intl.NumberFormat('fr-FR').format(price || 0)} {getDeviseActive()}
        </div>
      ),
    },
    {
      title: 'Performances',
      key: 'analytics',
      width: '15%',
      render: (_, record) => {
        const analytics = record.bundle_analytics || [];
        const totalViews = analytics.reduce((sum, a) => sum + (a.views_count || 0), 0);
        const totalPurchases = analytics.reduce((sum, a) => sum + (a.purchases_count || 0), 0);
        
        return (
          <div className="text-sm">
            <div>{totalViews} vues</div>
            <div className="text-green-600">{totalPurchases} achats</div>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '16%',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EyeIcon className="h-4 w-4" />} 
            onClick={() => navigate(`/app/bundles/${record.id}`)}
          />
          <Button 
            type="text" 
            icon={<PencilIcon className="h-4 w-4" />} 
            onClick={() => handleEditBundle(record)}
          />
          <Button 
            type="text" 
            icon={<DocumentDuplicateIcon className="h-4 w-4" />} 
            onClick={() => handleDuplicateBundle(record)}
          />
          <Button 
            type="text" 
            danger 
            icon={<TrashIcon className="h-4 w-4" />} 
            onClick={() => handleDeleteBundle(record)}
          />
        </Space>
      ),
    },
  ];

  /**
   * Configuration des onglets
   */
  const tabItems = [
    {
      key: 'list',
      label: (
        <span className="flex items-center">
          <Squares2X2Icon className="h-4 w-4 mr-2" />
          Liste des bundles
        </span>
      ),
      icon: <Squares2X2Icon className="h-5 w-5" />
    },
    {
      key: 'analytics',
      label: (
        <span className="flex items-center">
          <ChartBarIcon className="h-4 w-4 mr-2" />
          Analyses
        </span>
      ),
      icon: <ChartBarIcon className="h-5 w-5" />
    }
  ];

  return (
    <div className="p-6">
      {/* En-tête avec statistiques */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des bundles</h1>
            <p className="text-gray-600">Créez et gérez des offres groupées avec upsell et downsell</p>
          </div>
          <Button
            type="primary"
            icon={<PlusIcon className="h-4 w-4" />}
            onClick={handleNewBundle}
            size="large"
          >
            Nouveau bundle
          </Button>
        </div>

        {/* Cartes statistiques */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total bundles"
                value={stats.total_bundles}
                prefix={<Squares2X2Icon className="h-4 w-4 text-blue-600" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Bundles actifs"
                value={stats.active_bundles}
                prefix={<ShoppingBagIcon className="h-4 w-4 text-green-600" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total vues"
                value={stats.total_views}
                prefix={<EyeIcon className="h-4 w-4 text-purple-600" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total achats"
                value={stats.total_purchases}
                prefix={<ArrowTrendingUpIcon className="h-4 w-4 text-orange-600" />}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Barre d'onglets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabItems.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Contenu de l'onglet Liste */}
          {activeTab === 'list' && (
            <div>
              {/* Filtres */}
              <div className="mb-4 flex flex-wrap gap-4">
                <Search
                  placeholder="Rechercher un bundle..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onSearch={() => loadBundles()}
                  style={{ width: 250 }}
                />
                
                <Select
                  placeholder="Statut"
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  style={{ width: 120 }}
                  allowClear
                >
                  <Option value="active">Actif</Option>
                  <Option value="inactive">Inactif</Option>
                  <Option value="draft">Brouillon</Option>
                </Select>

                <Select
                  placeholder="Type de bundle"
                  value={filters.bundle_type}
                  onChange={(value) => setFilters(prev => ({ ...prev, bundle_type: value }))}
                  style={{ width: 140 }}
                  allowClear
                >
                  <Option value="standard">Standard</Option>
                  <Option value="promotional">Promotionnel</Option>
                  <Option value="seasonal">Saisonnier</Option>
                </Select>
              </div>

              {/* Table des bundles */}
              <Table
                columns={columns}
                dataSource={bundles}
                loading={loading}
                rowKey="id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total} bundles`,
                  onChange: (page, pageSize) => {
                    setPagination(prev => ({
                      ...prev,
                      current: page,
                      pageSize
                    }));
                  }
                }}
                scroll={{ x: 'max-content' }}
              />
            </div>
          )}

          {/* Contenu de l'onglet Analytics */}
          {activeTab === 'analytics' && (
            <BundleAnalytics bundles={bundles} />
          )}
        </div>
      </div>

      {/* Modal de formulaire bundle */}
      <Modal
        title={editingBundle ? 'Modifier le bundle' : 'Nouveau bundle'}
        open={showBundleForm}
        onCancel={() => {
          setShowBundleForm(false);
          setEditingBundle(null);
        }}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        <BundleForm
          bundle={editingBundle}
          onSave={handleSaveBundle}
          onCancel={() => {
            setShowBundleForm(false);
            setEditingBundle(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default Bundles;